import * as AlertDialog from '@radix-ui/react-alert-dialog'
import clsx from 'clsx'
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Info
} from 'lucide-react'

import { Button } from '@/shared/ui/Button'

import { closeDialog, useDialogStore, type DialogVariant } from './imperative'

/**
 * DialogHost — host dos diálogos imperativos (`confirm`/`alert`).
 *
 * Segue o mesmo padrão do `@/shared/ui/ConfirmDialog`: construído sobre
 * `@radix-ui/react-alert-dialog` e estilizado 100% com tokens do DS
 * (`bg-white`, `rounded-2xl`, `shadow-xl`, `Button` do DS) — não depende dos
 * tokens shadcn (`bg-background`/`--color-*`), ausentes neste stack Tailwind v4.
 * Renderiza em `z-dialog-backdrop`/`z-dialog`, acima do `MobileSheet`
 * (`z-sheet`). Escala em `styles/tailwind.css`.
 */

const variantIcon: Record<DialogVariant, typeof Info> = {
  default: Info,
  info: Info,
  destructive: AlertCircle,
  success: CheckCircle2,
  warning: AlertTriangle
}

const variantIconClass: Record<DialogVariant, string> = {
  default: 'text-brand',
  info: 'text-brand',
  destructive: 'text-red-500',
  success: 'text-green-500',
  warning: 'text-warning'
}

const variantIconBg: Record<DialogVariant, string> = {
  default: 'bg-brand/10',
  info: 'bg-brand/10',
  destructive: 'bg-red-100',
  success: 'bg-green-100',
  warning: 'bg-warning-light'
}

export function DialogHost() {
  const current = useDialogStore(s => s.current)
  const open = !!current

  if (!current) return null

  const variant = current.variant ?? 'default'
  const Icon = variantIcon[variant] ?? Info

  const showCancel =
    current.kind === 'confirm' && current.showCancel !== false
  const cancelLabel =
    current.kind === 'confirm' ? current.cancelLabel : undefined
  const confirmVariant =
    current.kind === 'confirm' && current.confirmVariant
      ? current.confirmVariant
      : variant === 'destructive'
      ? 'destructive'
      : 'default'

  return (
    <AlertDialog.Root
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) closeDialog(false)
      }}
    >
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 z-dialog-backdrop bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <AlertDialog.Content
          className={clsx(
            'fixed left-1/2 top-1/2 z-dialog -translate-x-1/2 -translate-y-1/2',
            'w-[calc(100%-2rem)] max-w-[400px] rounded-2xl bg-white p-6 shadow-xl outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
          )}
        >
          <div
            className={clsx(
              'mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full',
              variantIconBg[variant]
            )}
          >
            <Icon
              className={clsx('h-6 w-6', variantIconClass[variant])}
              aria-hidden
            />
          </div>

          <AlertDialog.Title className="block text-center text-2xl font-bold text-gray-900">
            {current.title}
          </AlertDialog.Title>

          {current.description ? (
            <AlertDialog.Description className="mt-3 block whitespace-pre-line text-center text-sm text-gray-700">
              {current.description}
            </AlertDialog.Description>
          ) : null}

          {current.content ? (
            <div className="mt-3 text-center text-sm text-gray-700">
              {current.content}
            </div>
          ) : null}

          <div className="mt-6 flex flex-col gap-3">
            <Button
              type="button"
              variant={confirmVariant === 'destructive' ? 'danger' : 'primary'}
              showTrailingIcon={false}
              className="w-full justify-center"
              onClick={() => closeDialog(true)}
            >
              {current.confirmLabel ?? 'OK'}
            </Button>
            {showCancel ? (
              <Button
                type="button"
                variant="secondary"
                showTrailingIcon={false}
                className="w-full justify-center"
                onClick={() => closeDialog(false)}
              >
                {cancelLabel ?? 'Cancelar'}
              </Button>
            ) : null}
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  )
}
